/**
 * E07-F02-T11 — which discovery searches failed, and why.
 *
 * The ZoomInfo discovery used to swallow a failed search (HTTP 400/401/429, an
 * unresolvable location) and the user saw an empty long-list with no reason. The
 * server now records every failed search on the run (`job.discoveryIssues`,
 * research-orchestrator contract) and this banner lists them:
 *   - `variant="warning"` (Review) — SOME searches failed, the list is partial;
 *   - `variant="error"` (Run, failed state) — every search failed.
 * Nothing here retries: the reason tells the human what to change.
 */
import type { JSX } from 'react';
import { discoveryTileLabel, isDiscoveryNotice, type DiscoveryIssue } from '@eer/shared';
import { useResearchStatus } from './researchOrchestratorApi.js';

export interface DiscoveryIssuesBannerProps {
  issues: readonly DiscoveryIssue[];
  variant: 'warning' | 'error';
  testId?: string;
}

export function DiscoveryIssuesBanner({
  issues,
  variant,
  testId = 'discovery-issues',
}: DiscoveryIssuesBannerProps): JSX.Element | null {
  if (issues.length === 0) return null;
  const tone =
    variant === 'error'
      ? 'bg-red-50 border-red-200 text-red-800'
      : 'bg-amber-50 border-amber-200 text-amber-800';
  // E07-F02-T14 — "found nothing, here is why" notices are not failed searches.
  const failed = issues.filter((i) => !isDiscoveryNotice(i)).length;
  const empty = issues.length - failed;
  const heading =
    variant === 'error'
      ? 'Company search failed — no companies could be discovered.'
      : failed > 0
        ? `Company search was incomplete — ${failed} ${
            failed === 1 ? 'search' : 'searches'
          } failed, so this list may be missing companies.`
        : `${empty} ${empty === 1 ? 'search' : 'searches'} found no companies — the reasons are below.`;
  return (
    <div
      data-testid={testId}
      data-variant={variant}
      role={variant === 'error' ? 'alert' : 'status'}
      className={`border rounded-lg p-4 mb-4 text-sm ${tone}`}
    >
      <div className="font-bold mb-1">
        <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden /> {heading}
      </div>
      <ul className="list-disc pl-5 space-y-0.5">
        {issues.map((issue, i) => (
          <li
            key={`${discoveryTileLabel(issue.tile)}-${i}`}
            data-testid="discovery-issue"
            data-code={issue.code}
          >
            <b>{discoveryTileLabel(issue.tile)}:</b> {issue.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Review-screen warning: reads the run snapshot's failed searches (partial discovery). */
export function ReviewDiscoveryWarning({ requestId }: { requestId: number }): JSX.Element | null {
  const { data } = useResearchStatus(requestId);
  const issues = data?.job.discoveryIssues ?? [];
  return (
    <DiscoveryIssuesBanner issues={issues} variant="warning" testId="review-discovery-warning" />
  );
}
